import { useState, useEffect, lazy, Suspense, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  AreaChart, Area,
} from 'recharts'
import {
  Calendar, SlidersHorizontal, Plus, Bell, HelpCircle, Edit3,
  MoreVertical, TrendingUp, TrendingDown, ChevronDown, Maximize2,
  Shield, AlertTriangle, ClipboardList, FileCheck, Clock, Activity,
  Sparkles, MapPin, BarChart2,
} from 'lucide-react'
import api from '../../services/api'
import AnalyticsList from './List'

const HeatMap = lazy(() => import('./HeatMap'))

const TEAL  = '#00C4A1'
const NAVY  = '#0D1B3E'
const RED   = '#DC2626'
const ORANGE = '#F5921E'

const DONUT_COLORS = [TEAL, ORANGE, '#2D7DD2', '#6554C0', '#94A3B8', '#3DB54A']

const TABS = ['Overview', 'Safety', 'Operations', 'People', 'Environment', 'Custom Dashboards'] as const
type Tab = typeof TABS[number]

// ── helpers ───────────────────────────────────────────────────────────────────

function getTrifrColor(v: number): string {
  if (v >= 1.5) return '#DC2626'
  if (v >= 1.0) return '#EF4444'
  if (v >= 0.5) return '#F5921E'
  return '#22C55E'
}

function fmt(v: number | null, decimals = 0): string {
  if (v === null || v === undefined) return '—'
  return Number(v).toFixed(decimals)
}

// ── Sparkline ─────────────────────────────────────────────────────────────────

function Sparkline({ data, color = TEAL }: { data: number[]; color?: string }) {
  if (!data || data.length < 2) return <div style={{ width: 80, height: 32 }} />
  const pts = data.map(v => ({ v }))
  return (
    <ResponsiveContainer width={80} height={32}>
      <AreaChart data={pts} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
        <defs>
          <linearGradient id={`sg-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.3} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5}
          fill={`url(#sg-${color.replace('#','')})`} dot={false} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  )
}

// ── KPI card ──────────────────────────────────────────────────────────────────

interface KPIData {
  value: number
  change: number
  spark: number[]
}

const KPI_META: Record<string, { label: string; icon: any; format: (v: number) => string; goodDown: boolean; iconBg: string; iconColor: string }> = {
  trifr:            { label: 'TRIFR (YTD)',       icon: Activity,       format: v => fmt(v, 2), goodDown: true,  iconBg: '#E6FAF6', iconColor: TEAL    },
  ltifr:            { label: 'LTIFR (YTD)',        icon: Shield,         format: v => fmt(v, 2), goodDown: true,  iconBg: '#FEF3C7', iconColor: '#D97706' },
  open_incidents:   { label: 'Open Incidents',     icon: AlertTriangle,  format: v => String(v),  goodDown: false, iconBg: '#FEE2E2', iconColor: RED      },
  overdue_actions:  { label: 'Overdue Actions',    icon: Clock,          format: v => String(v),  goodDown: true,  iconBg: '#FEE2E2', iconColor: RED      },
  audit_compliance: { label: 'Audit Compliance',   icon: FileCheck,      format: v => `${fmt(v, 1)}%`, goodDown: false, iconBg: '#E6FAF6', iconColor: TEAL },
  active_permits:   { label: 'Active Permits',     icon: ClipboardList,  format: v => String(v),  goodDown: false, iconBg: '#EEF2FF', iconColor: '#6366F1' },
}

function KPICard({ id, data }: { id: string; data: KPIData }) {
  const meta = KPI_META[id]
  if (!meta) return null
  const Icon = meta.icon
  const isGood = (meta.goodDown && data.change <= 0) || (!meta.goodDown && data.change >= 0)
  const changeColor = data.change === 0 ? '#94A3B8' : isGood ? '#22C55E' : RED
  const sparkColor = isGood ? TEAL : (meta.goodDown ? RED : RED)
  const ArrowIcon = data.change > 0 ? TrendingUp : TrendingDown

  return (
    <div className="rounded-xl p-4 flex flex-col gap-3" style={{ background: '#fff', border: '1px solid var(--border)', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: meta.iconBg }}>
            <Icon size={15} style={{ color: meta.iconColor }} />
          </div>
          <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{meta.label}</span>
        </div>
        <Sparkline data={data.spark} color={sparkColor} />
      </div>
      <div>
        <div className="text-2xl font-extrabold leading-none" style={{ color: 'var(--text)' }}>
          {meta.format(data.value)}
        </div>
        <div className="flex items-center gap-1 mt-1.5">
          <ArrowIcon size={11} style={{ color: changeColor }} />
          <span className="text-xs font-medium" style={{ color: changeColor }}>
            {Math.abs(data.change)}% vs last period
          </span>
        </div>
      </div>
    </div>
  )
}

// ── Dark tooltip ──────────────────────────────────────────────────────────────

function DarkTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: NAVY, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
      {label && <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 4, fontSize: 11 }}>{label}</p>}
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color || '#fff', fontWeight: 600 }}>
          {p.name ? `${p.name}: ` : ''}{typeof p.value === 'number' ? p.value.toFixed(p.value % 1 ? 2 : 0) : p.value}
        </p>
      ))}
    </div>
  )
}

// ── Incident Trend chart ──────────────────────────────────────────────────────

function IncidentTrendChart({ data }: { data: { period: string; current: number; previous: number }[] }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -20 }}>
        <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip content={<DarkTooltip />} />
        <Line type="monotone" dataKey="current"  name="This Period" stroke={TEAL}    strokeWidth={2} dot={{ r: 3, fill: TEAL }} activeDot={{ r: 5 }} />
        <Line type="monotone" dataKey="previous" name="Last Period"  stroke="#94A3B8" strokeWidth={1.5} dot={false} strokeDasharray="4 3" />
      </LineChart>
    </ResponsiveContainer>
  )
}

// ── Incidents by type donut ────────────────────────────────────────────────────

function IncidentsByTypeChart({ rows, total }: { rows: { name: string; value: number }[]; total: number }) {
  return (
    <div className="flex items-center gap-4 h-full">
      <div style={{ width: 160, height: '100%', flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={rows} dataKey="value" nameKey="name" cx="50%" cy="50%"
              outerRadius="72%" innerRadius="42%" strokeWidth={2} stroke="white">
              {rows.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
            </Pie>
            <text x="50%" y="46%" textAnchor="middle" style={{ fill: '#64748B', fontSize: 11, fontWeight: 600 }}>Total</text>
            <text x="50%" y="60%" textAnchor="middle" style={{ fill: NAVY, fontSize: 20, fontWeight: 800 }}>{total}</text>
            <Tooltip content={<DarkTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex-1 space-y-2 overflow-hidden">
        {rows.map((r, i) => (
          <div key={r.name} className="flex items-center gap-2 text-xs">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
            <span className="flex-1 truncate" style={{ color: 'var(--text)' }}>{r.name}</span>
            <span className="font-medium flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
              {total > 0 ? Math.round(r.value / total * 100) : 0}% ({r.value})
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Top sites horizontal bar ──────────────────────────────────────────────────

function TopSitesChart({ sites }: { sites: { site: string; trifr: number }[] }) {
  const maxVal = Math.max(...sites.map(s => s.trifr), 0.1)
  return (
    <div className="space-y-3 h-full overflow-auto">
      {sites.map(s => (
        <div key={s.site} className="flex items-center gap-3">
          <div className="text-xs font-medium truncate" style={{ color: 'var(--text)', width: 110, flexShrink: 0 }}>{s.site}</div>
          <div className="flex-1 h-5 rounded-full overflow-hidden" style={{ background: '#F1F5F9' }}>
            <div className="h-full rounded-full transition-all" style={{
              width: `${(s.trifr / maxVal) * 100}%`,
              background: getTrifrColor(s.trifr),
              minWidth: 4,
            }} />
          </div>
          <div className="text-xs font-bold flex-shrink-0 w-10 text-right" style={{ color: getTrifrColor(s.trifr) }}>
            {s.trifr.toFixed(2)}
          </div>
        </div>
      ))}
      <div className="flex justify-between text-xs mt-1" style={{ color: '#94A3B8' }}>
        <span>0</span>
        <span>{(maxVal / 2).toFixed(1)}</span>
        <span>{maxVal.toFixed(1)}</span>
      </div>
    </div>
  )
}

// ── Audit compliance table ─────────────────────────────────────────────────────

function AuditComplianceTable({ rows }: { rows: { site: string; compliance: number; change: number }[] }) {
  return (
    <div className="space-y-0">
      <div className="grid text-xs font-semibold uppercase tracking-wide mb-2" style={{ gridTemplateColumns: '1fr auto auto', color: '#94A3B8', gap: '0 12px' }}>
        <span>Site</span>
        <span>Compliance</span>
        <span>vs Last Period</span>
      </div>
      {rows.map(r => {
        const color = r.compliance >= 90 ? '#22C55E' : r.compliance >= 75 ? ORANGE : RED
        return (
          <div key={r.site} className="py-2.5 border-t" style={{ borderColor: 'var(--border)', display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0 12px', alignItems: 'center' }}>
            <span className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{r.site}</span>
            <div className="flex items-center gap-2">
              <div style={{ width: 80, height: 6, background: '#F1F5F9', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${r.compliance}%`, height: '100%', background: color, borderRadius: 3 }} />
              </div>
              <span className="text-xs font-bold" style={{ color, minWidth: 36 }}>{r.compliance}%</span>
            </div>
            <div className="flex items-center gap-0.5 text-xs font-medium justify-end" style={{ color: r.change >= 0 ? '#22C55E' : RED, minWidth: 44 }}>
              {r.change >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
              <span>{Math.abs(r.change)}%</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Safety Intelligence panel ──────────────────────────────────────────────────

function deriveInsights(kpis: any, topSites: any[], auditBySite: any[]): { icon: any; color: string; text: ReactNode }[] {
  const insights: { icon: any; color: string; text: ReactNode }[] = []

  if (kpis?.open_incidents?.change > 0) {
    insights.push({
      icon: TrendingUp, color: RED,
      text: <>Incident frequency has increased <strong style={{ color: RED }}>{kpis.open_incidents.change}%</strong> compared to last period.</>
    })
  }
  if (topSites.length > 0) {
    insights.push({
      icon: MapPin, color: '#6366F1',
      text: <><strong>{topSites[0].site}</strong> has the highest incident frequency this month.</>
    })
  }
  if (kpis?.overdue_actions?.value > 0) {
    insights.push({
      icon: Clock, color: ORANGE,
      text: <><strong>{kpis.overdue_actions.value}</strong> action{kpis.overdue_actions.value !== 1 ? 's are' : ' is'} overdue by more than 30 days.</>
    })
  }
  if (kpis?.audit_compliance?.change !== 0) {
    const dir = kpis.audit_compliance.change > 0 ? 'improved' : 'decreased'
    const color = kpis.audit_compliance.change > 0 ? TEAL : RED
    insights.push({
      icon: BarChart2, color,
      text: <>Audit compliance has <strong>{dir}</strong> {Math.abs(kpis.audit_compliance.change)}% compared to last period.</>
    })
  }

  if (insights.length === 0) {
    insights.push({ icon: Shield, color: TEAL, text: 'No critical safety alerts. All metrics within target range.' })
  }
  return insights.slice(0, 4)
}

function SafetyIntelligence({ kpis, topSites, auditBySite }: { kpis: any; topSites: any[]; auditBySite: any[] }) {
  const insights = deriveInsights(kpis, topSites, auditBySite)
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.1)' }}>
          <Sparkles size={16} style={{ color: '#6366F1' }} />
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Safety Intelligence</span>
          <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(99,102,241,0.1)', color: '#6366F1' }}>BETA</span>
        </div>
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>AI insights for your organisation</p>
      <div className="space-y-4 flex-1">
        {insights.map((ins, i) => {
          const Icon = ins.icon
          return (
            <div key={i} className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${ins.color}18` }}>
                <Icon size={13} style={{ color: ins.color }} />
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>{ins.text}</p>
            </div>
          )
        })}
      </div>
      <button className="flex items-center gap-1.5 mt-4 text-sm font-medium pt-4 border-t" style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}>
        View all insights <ChevronDown size={13} style={{ transform: 'rotate(-90deg)' }} />
      </button>
    </div>
  )
}

// ── Section card wrapper ──────────────────────────────────────────────────────

function SCard({ title, action, children, style }: { title: string; action?: ReactNode; children: ReactNode; style?: React.CSSProperties }) {
  return (
    <div className="rounded-xl flex flex-col" style={{ background: '#fff', border: '1px solid var(--border)', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', padding: 20, ...style }}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{title}</span>
        {action}
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  )
}

function ViewFullAction() {
  return (
    <button className="flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
      View full report <Maximize2 size={11} />
    </button>
  )
}

// ── Overview tab content ──────────────────────────────────────────────────────

function OverviewContent({ data, loading }: { data: any; loading: boolean }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading analytics…</div>
      </div>
    )
  }
  if (!data) return null

  const { kpis, trend, by_type, top_sites, audit_by_site } = data

  return (
    <div className="space-y-5">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {Object.entries(kpis).map(([id, d]) => (
          <KPICard key={id} id={id} data={d as KPIData} />
        ))}
      </div>

      {/* Row 2: trend | donut | top sites */}
      <div className="grid gap-5" style={{ gridTemplateColumns: '5fr 4fr 3fr' }}>
        <SCard title="Incident Trend" action={
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border" style={{ color: 'var(--text)', borderColor: 'var(--border)' }}>
              Monthly <ChevronDown size={11} />
            </button>
            <ViewFullAction />
          </div>
        }>
          <div className="flex items-center gap-4 mb-3 text-xs">
            <div className="flex items-center gap-1.5"><div className="w-5 h-0.5 rounded" style={{ background: TEAL }} /><span style={{ color: 'var(--text-muted)' }}>This Period</span></div>
            <div className="flex items-center gap-1.5"><div className="w-5 h-0.5 rounded border-t-2 border-dashed" style={{ borderColor: '#94A3B8' }} /><span style={{ color: 'var(--text-muted)' }}>Last Period</span></div>
          </div>
          <div style={{ height: 200 }}>
            <IncidentTrendChart data={trend} />
          </div>
        </SCard>

        <SCard title="Incidents by Type" action={<ViewFullAction />}>
          <div style={{ height: 240 }}>
            <IncidentsByTypeChart rows={by_type.rows} total={by_type.total} />
          </div>
        </SCard>

        <SCard title="Top Sites by Incident Frequency (YTD)" action={<ViewFullAction />}>
          <div style={{ height: 240 }}>
            <TopSitesChart sites={top_sites} />
          </div>
        </SCard>
      </div>

      {/* Row 3: heat map | audit compliance | safety intelligence */}
      <div className="grid gap-5" style={{ gridTemplateColumns: '5fr 4fr 3fr' }}>
        <SCard title="Incidents Heat Map" action={<ViewFullAction />} style={{ minHeight: 360 }}>
          <div style={{ height: 300, borderRadius: 8, overflow: 'hidden' }}>
            <Suspense fallback={<div className="flex items-center justify-center h-full text-sm" style={{ color: 'var(--text-muted)' }}>Loading map…</div>}>
              <HeatMap sites={top_sites} />
            </Suspense>
          </div>
        </SCard>

        <SCard title="Audit Compliance by Site" action={<ViewFullAction />}>
          <AuditComplianceTable rows={audit_by_site} />
        </SCard>

        <SCard title="" action={null}>
          <SafetyIntelligence kpis={kpis} topSites={top_sites} auditBySite={audit_by_site} />
        </SCard>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AnalyticsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('Overview')
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const navigate = useNavigate()

  const fetchData = () => {
    setLoading(true)
    api.get('/metrics/analytics-overview')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchData() }, [])

  useEffect(() => {
    if (!autoRefresh) return
    const id = setInterval(fetchData, 30000)
    return () => clearInterval(id)
  }, [autoRefresh])

  const now = new Date()
  const yearStart = new Date(now.getFullYear(), 0, 1)
  const fmtDate = (d: Date) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })

  return (
    <div className="flex flex-col" style={{ minHeight: '100%', background: 'var(--bg)' }}>
      {/* Page header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b" style={{ borderColor: 'var(--border)' }}>
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text)' }}>Analytics</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Transform your HSEQ data into actionable insights.</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm" style={{ color: 'var(--text)', borderColor: 'var(--border)', background: 'white' }}>
            <Calendar size={14} style={{ color: 'var(--text-muted)' }} />
            <span>{fmtDate(yearStart)} – {fmtDate(now)}</span>
            <ChevronDown size={13} style={{ color: 'var(--text-muted)' }} />
          </button>
          <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm" style={{ color: 'var(--text)', borderColor: 'var(--border)', background: 'white' }}>
            <SlidersHorizontal size={14} /> Filters
          </button>
          <button
            onClick={() => setActiveTab('Custom Dashboards')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: TEAL }}
          >
            <Plus size={14} /> New Dashboard
          </button>
          <button className="relative p-2 rounded-lg" style={{ color: 'var(--text-muted)', background: 'white', border: '1px solid var(--border)' }}>
            <Bell size={16} />
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-white flex items-center justify-center" style={{ background: RED, fontSize: 9, fontWeight: 700 }}>3</span>
          </button>
          <button className="p-2 rounded-lg" style={{ color: 'var(--text-muted)', background: 'white', border: '1px solid var(--border)' }}>
            <HelpCircle size={16} />
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center justify-between bg-white border-b px-6" style={{ borderColor: 'var(--border)' }}>
        <div className="flex">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
              style={{
                borderBottomColor: activeTab === tab ? TEAL : 'transparent',
                color: activeTab === tab ? TEAL : 'var(--text-muted)',
              }}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Auto refresh</span>
            <button
              onClick={() => setAutoRefresh(r => !r)}
              className="relative w-9 h-5 rounded-full transition-colors"
              style={{ background: autoRefresh ? TEAL : '#E2E8F0' }}
              aria-label="Toggle auto refresh"
            >
              <div className="absolute w-3.5 h-3.5 rounded-full bg-white top-0.5 transition-transform"
                style={{ transform: autoRefresh ? 'translateX(20px)' : 'translateX(2px)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </button>
            <span className="text-xs font-medium" style={{ color: autoRefresh ? TEAL : 'var(--text-muted)' }}>{autoRefresh ? 'On' : 'Off'}</span>
          </div>
          <button className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border" style={{ color: 'var(--text)', borderColor: 'var(--border)' }}>
            <Edit3 size={12} /> Edit Dashboard
          </button>
          <button className="p-1.5 rounded-lg" style={{ color: 'var(--text-muted)' }}>
            <MoreVertical size={15} />
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 p-6">
        {activeTab === 'Overview' && <OverviewContent data={data} loading={loading} />}

        {activeTab === 'Custom Dashboards' && (
          <div className="max-w-5xl mx-auto">
            <AnalyticsList />
          </div>
        )}

        {activeTab !== 'Overview' && activeTab !== 'Custom Dashboards' && (
          <div className="flex flex-col items-center justify-center py-24 space-y-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'var(--accent-light)' }}>
              <Activity size={28} style={{ color: TEAL }} />
            </div>
            <p className="font-semibold text-lg" style={{ color: 'var(--text)' }}>{activeTab} Analytics</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Coming soon — this view is under development.</p>
          </div>
        )}
      </div>
    </div>
  )
}
